import { defineMessages, useIntl } from 'react-intl';
import cx from 'classnames';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import BlockDataForm from '@plone/volto/components/manage/Form/BlockDataForm';
import config from '@plone/registry';
import {
  TextEditorWidget,
  useHandleDetachedBlockFocus,
} from '@redturtle/volto-slate-extras';
import type { BlockEditProps } from '@plone/types';
import styles from '@redturtle/qubicaamf-theme/components/Blocks/ImageGallery/styles.module.css';
import '@redturtle/qubicaamf-theme/components/Blocks/ImageGallery/editor.scss';
import Slides from '@redturtle/qubicaamf-theme/components/Blocks/ImageGallery/Slides';
import type { ImageGalleryData } from '@redturtle/qubicaamf-theme/components/Blocks/ImageGallery/schema';
import CTA from '../commons/CTA';

type ImageGalleryEditProps = Omit<BlockEditProps, 'data'> & {
  data: ImageGalleryData;
};

const Edit = (props: ImageGalleryEditProps) => {
  const { selected, blocksConfig, onChangeBlock, block, data } = props;
  const intl = useIntl();

  const schema = blocksConfig[data['@type']].blockSchema({
    data,
    intl,
  });

  const { selectedField, setSelectedField } = useHandleDetachedBlockFocus(
    props,
    'title',
  );

  const Container = config.getComponent('Container').component || 'div';

  return (
    <>
      <section
        className={cx('block-imagegallery', styles.block)}
        aria-label={data.title}
      >
        {!data?.styles?.fullwidth ? (
          <Container
            className={cx('block-imagegallery-container', styles.container)}
          >
            <div className={styles.titleContainer}>
              <TextEditorWidget
                {...props}
                as="h2"
                data={data}
                fieldName="title"
                selected={selected && selectedField === 'title'}
                setSelected={setSelectedField}
                focusNextField={() => {
                  setSelectedField('text');
                }}
                showToolbar={false}
                placeholder={intl.formatMessage(messages.title)}
              />
            </div>
            <div className={styles.description}>
              <TextEditorWidget
                {...props}
                fieldName="text"
                selected={selected && selectedField === 'text'}
                setSelected={setSelectedField}
                focusPrevField={() => {
                  setSelectedField('title');
                }}
                placeholder={intl.formatMessage(messages.text)}
              />
            </div>
            {data?.slides?.length > 0 ? (
              <Slides slides={data.slides} editMode={true} />
            ) : (
              <div className={styles.placeholder}>
                {intl.formatMessage(messages.help_text)}
              </div>
            )}
          </Container>
        ) : (
          <div className="block-imagegallery-container">
            <div className={styles.titleContainer}>
              <TextEditorWidget
                {...props}
                as="h2"
                data={data}
                fieldName="title"
                selected={selected && selectedField === 'title'}
                setSelected={setSelectedField}
                focusNextField={() => {
                  setSelectedField('text');
                }}
                showToolbar={false}
                placeholder={intl.formatMessage(messages.title)}
              />
            </div>
            <div className={styles.description}>
              <TextEditorWidget
                {...props}
                fieldName="text"
                selected={selected && selectedField === 'text'}
                setSelected={setSelectedField}
                focusPrevField={() => {
                  setSelectedField('title');
                }}
                placeholder={intl.formatMessage(messages.text)}
              />
            </div>
            {data?.slides?.length > 0 ? (
              <Slides slides={data.slides} editMode={true} />
            ) : (
              <div className={styles.placeholder}>
                {intl.formatMessage(messages.help_text)}
              </div>
            )}
          </div>
        )}
        {data.linkHref?.[0] && (
          <div className={styles.ctaContainer}>
            <CTA
              href={data.linkHref[0]['@id']}
              onClick={(e: React.SyntheticEvent<HTMLLinkElement>) => {
                e.preventDefault();
              }}
              openLinkInNewTab={false}
              ctaButtonStyle={data.ctaButtonStyle}
              linkIcon={data.linkIcon}
              cta_color={data.cta_color}
            >
              {data.linkTitle}
            </CTA>
          </div>
        )}
      </section>
      {/* @ts-expect-error */}
      <SidebarPortal selected={selected}>
        <BlockDataForm
          schema={schema}
          title={schema.title}
          onChangeField={(id, value) => {
            onChangeBlock(block, {
              ...data,
              [id]: value,
            });
          }}
          formData={data}
          block={block}
        />
      </SidebarPortal>
    </>
  );
};

export default Edit;

const messages = defineMessages({
  title: {
    id: 'Title',
    defaultMessage: 'Title',
  },
  text: {
    id: 'Text',
    defaultMessage: 'Text',
  },
  help_text: {
    id: 'help_text',
    defaultMessage: 'Add slides from the sidebar',
  },
});
